/**
 * ReportsHarvestInspectionsTable Selectors
 */
import { createSelectorCreator, defaultMemoize } from 'reselect';
import isEqual from 'lodash.isequal';
import search from 'javascript-array-search';

// Selectors
import { selectReportsHarvestInspectionsArray } from '../../../../services/reportsHarvestInspections/selectors';

// Workspaces
import {
    makeSelectQueryStringValue,
    makeSelectQueryStringNumberValue
} from '../../../../workspaces/react-router-selectors';

// Utils
import orderBy from '../../../../utils/custom-orderBy';

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

type State = *;

interface Props {
    defaultCount: number;
    defaultSort: string;
}

const selectDefaultCount = (state: State, props: Props) => props.defaultCount;
const selectDefaultSort = (state: State, props: Props) => props.defaultSort;

const selectDenormalizedData = createDeepEqualSelector(
    [selectReportsHarvestInspectionsArray],
    reportsHarvestInspectionsArrayState =>
        reportsHarvestInspectionsArrayState.map(item => ({
            ...item,
            varietyName: item.varietyName || item.commodityName
        }))
);

const selectFilteredData = createDeepEqualSelector(
    [
        selectDenormalizedData,
        makeSelectQueryStringValue('search'),
        makeSelectQueryStringValue('pickTypeName'),
        makeSelectQueryStringValue('varieties'),
        makeSelectQueryStringNumberValue('entities')
    ],
    (
        dataState,
        searchState,
        pickTypeNameState,
        varietiesState,
        entitiesState
    ) => {
        const searchedData = search(dataState, searchState, [
            'parentEntityName',
            'entityName'
        ]);
        return searchedData.filter(value => {
            if (!value.event || !value.event.startDate) return false;
            const { pickTypeName, varietyId, parentEntityId } = value;
            if (pickTypeNameState && pickTypeNameState !== pickTypeName) {
                return false;
            }
            if (
                varietiesState &&
                !varietiesState.includes(varietyId.toString())
            ) {
                return false;
            }
            if (entitiesState && entitiesState !== parentEntityId) {
                return false;
            }
            return value;
        });
    }
);

const selectDataLength = createDeepEqualSelector(
    selectFilteredData,
    filteredDataState => filteredDataState.length
);

const selectOrderedData = createDeepEqualSelector(
    [selectFilteredData, makeSelectQueryStringValue('sort'), selectDefaultSort],
    (filteredDataState, sortState, defaultSortState) => {
        const sort = sortState || defaultSortState;
        const attributes = sort.split('_');
        const propertyName = attributes[0];
        const order = attributes[1] !== 'descending' ? 'asc' : 'desc';

        return orderBy(filteredDataState, propertyName, order);
    }
);

const selectPaginatedData = createDeepEqualSelector(
    [
        selectOrderedData,
        makeSelectQueryStringNumberValue('page'),
        makeSelectQueryStringNumberValue('count'),
        selectDefaultCount
    ],
    (orderedDataState, pageState = 1, countState, defaultCountState) => {
        const count = countState || defaultCountState;
        const begin = (pageState - 1) * count;
        const end = pageState * count;
        return orderedDataState.slice(begin, end);
    }
);

const selectIds = createDeepEqualSelector(
    selectPaginatedData,
    paginatedDataState =>
        paginatedDataState && paginatedDataState.map(value => value.eventId)
);

export { selectDataLength, selectFilteredData, selectOrderedData, selectIds };
